import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { Box, Link, Stack, ThemeProvider, Typography } from '@mui/material';

import { useUser } from '@mopla/business-logic';
import { passengerThemeV2 } from '@mopla/mui';
import {
	DTicketPageLayout,
	EMlpMailIconVariant,
	MailIcon,
	MplButton,
	MplOperatorWordmark,
	StyledDTicketPageLayoutFooter,
} from '@mopla/ui';

import { usePassengerAppNavigate } from '../../../hooks';
import { useMyTicketDetail } from '../myTicketDetail/hooks';
import { isChipcardEligible } from '../myTicketDetail/isChipcardEligible';

import { buildChipcardMailtoHref, LSE_CONTACT } from './chipcardEmail';
import { ChipcardRequestSkeleton } from './ChipcardRequestSkeleton';

/**
 * "Chipkarte beantragen" screen reached from the "Ticket als Chipkarte erhalten"
 * entry on a ticket the BE flags as chipcard-eligible (`eligibilityForChipcard`).
 *
 * Shows the operator wordmark, the user's mopla email + ticket ID, the LSE
 * contact block, and a button that opens the device email client with a
 * prefilled request (Figma 25262-29772 / 25262-37154). Only reachable for a
 * ticket that qualifies for the chipcard option; anyone else is bounced home.
 */
export const ChipcardRequest = () => {
	const { t } = useTranslation('ticketshop');
	const { purchaseId } = useParams<{ purchaseId: string }>();
	const location = useLocation();
	const { navigateBack, navigateToHome, navigateToMyTicketDetail } =
		usePassengerAppNavigate();

	const { ticket, isLoading } = useMyTicketDetail(purchaseId);
	const { userData } = useUser();

	const eligible = !!ticket && isChipcardEligible(ticket);

	// This screen is pushed onto the in-app history, so Back pops that entry to
	// return to the detail we came from. Pushing a fresh detail instead would
	// leave detail on top of chipcard, so the detail's own Back would land back
	// here (MC-11409). `key === 'default'` means a deep-link / refresh with no
	// in-app history to pop — then navigate explicitly (replace, so the disallowed
	// chipcard URL leaves the stack), falling back home without a purchaseId.
	const handleBack = useCallback(() => {
		if (location.key !== 'default') {
			navigateBack();
		} else if (purchaseId) {
			navigateToMyTicketDetail(purchaseId, { replace: true });
		} else {
			navigateToHome({ replace: true });
		}
	}, [
		location.key,
		navigateBack,
		purchaseId,
		navigateToMyTicketDetail,
		navigateToHome,
	]);

	// Access scope (AC): once loading settles, only a qualifying ticket may stay.
	// A resolvable but ineligible ticket returns to its detail; a ticket that
	// can't be resolved (bad id) bails home. `replace` keeps the disallowed
	// /chipcard URL out of history so Back doesn't bounce straight back here.
	useEffect(() => {
		if (isLoading) {
			return;
		}
		if (!purchaseId || !ticket) {
			navigateToHome({ replace: true });
		} else if (!isChipcardEligible(ticket)) {
			navigateToMyTicketDetail(purchaseId, { replace: true });
		}
	}, [isLoading, ticket, purchaseId, navigateToHome, navigateToMyTicketDetail]);

	const handleEmailForm = useCallback(() => {
		if (ticket) {
			window.location.href = buildChipcardMailtoHref(ticket, userData?.email);
		}
	}, [ticket, userData?.email]);

	return (
		<ThemeProvider theme={passengerThemeV2}>
			<DTicketPageLayout
				title={t('chipcard_request.title')}
				onBack={handleBack}
				scrollable
				stickyHeader
				dataTestId="chipcard-request-page"
			>
				{!eligible || !ticket ? (
					<ChipcardRequestSkeleton />
				) : (
					<>
						<MplOperatorWordmark
							src={ticket.operatorWordmarkUrl}
							dataTestId="chipcard-request-wordmark"
						/>

						{/* Reserve space for the fixed footer button. */}
						<Stack gap={3} sx={{ width: '100%', pt: 2, pb: '96px' }}>
							<Typography
								variant="b2"
								color="primary.main"
								sx={{ whiteSpace: 'pre-line' }}
							>
								{t('chipcard_request.description')}
							</Typography>

							{/* Customer email */}
							<Stack gap={0.5} sx={{ py: 0.5 }}>
								<Typography variant="b2" color="solutions.600">
									{t('chipcard_request.customer_email_label')}
								</Typography>
								<Typography variant="b1" color="primary.main">
									{userData?.email}
								</Typography>
							</Stack>

							{/* Ticket ID + operator (bus) icon */}
							<Stack
								direction="row"
								gap={2}
								alignItems="center"
								sx={{ py: 0.5 }}
							>
								<Stack gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
									<Typography variant="b2" color="solutions.600">
										{t('chipcard_request.ticket_id_label')}
									</Typography>
									<Typography variant="b1" color="primary.main">
										{ticket.ticketCode}
									</Typography>
								</Stack>
								{ticket.operatorIconUrl && (
									<Box
										component="img"
										src={ticket.operatorIconUrl}
										alt=""
										sx={{
											width: 48,
											height: 48,
											borderRadius: '24px',
											objectFit: 'cover',
											flexShrink: 0,
										}}
										data-testid="chipcard-request-operator-icon"
									/>
								)}
							</Stack>

							{/* LSE contact data */}
							<Stack gap={0.5}>
								<Typography variant="l1" color="solutions.600">
									{t('chipcard_request.contact_label')}
								</Typography>
								<Typography
									variant="b2"
									color="primary.main"
									sx={{ fontWeight: 700 }}
								>
									{LSE_CONTACT.name}
								</Typography>
								{LSE_CONTACT.addressLines.map((line) => (
									<Typography key={line} variant="b2" color="primary.main">
										{line}
									</Typography>
								))}
								<Typography variant="b2" color="primary.main" sx={{ mt: 1.5 }}>
									{t('chipcard_request.phone_prefix')}{' '}
									<Link
										href={LSE_CONTACT.phoneHref}
										underline="always"
										sx={{ color: 'primary.main' }}
										data-testid="chipcard-request-phone"
									>
										{LSE_CONTACT.phoneDisplay}
									</Link>
								</Typography>
								<Link
									href={`mailto:${LSE_CONTACT.email}`}
									underline="always"
									variant="b2"
									sx={{
										mt: 1.5,
										alignSelf: 'flex-start',
										color: 'primary.main',
									}}
									data-testid="chipcard-request-email"
								>
									{LSE_CONTACT.email}
								</Link>
							</Stack>
						</Stack>
					</>
				)}
			</DTicketPageLayout>

			{eligible && ticket && (
				<StyledDTicketPageLayoutFooter>
					<MplButton
						variant="contained"
						color="primary"
						fullWidth
						startIcon={
							<MailIcon variant={EMlpMailIconVariant.Secondary} size={24} />
						}
						onClick={handleEmailForm}
						testId="chipcard-request-email-button"
					>
						{t('chipcard_request.email_form_cta')}
					</MplButton>
				</StyledDTicketPageLayoutFooter>
			)}
		</ThemeProvider>
	);
};
